(() => {
  'use strict';

  const STORAGE_KEY = 'theme:selected-vehicle';
  const FILTERS = {
    make: 'filter.p.m.custom.vehicle_manufacturer',
    model: 'filter.p.m.custom.vehicle_model',
    year: 'filter.p.m.custom.vehicle_years',
    engine: 'filter.p.m.custom.vehicle_engine'
  };

  const normalize = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  class VehicleFinder extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.make = this.querySelector('[data-vehicle-make]');
      this.model = this.querySelector('[data-vehicle-model]');
      this.year = this.querySelector('[data-vehicle-year]');
      this.engine = this.querySelector('[data-vehicle-engine], [data-vehicle-variant]');
      this.natural = this.querySelector('[data-vehicle-natural]');
      this.status = this.querySelector('[data-vehicle-status]');
      this.data = [];
    }

    connectedCallback() {
      if (!this.form) return;
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('change', this.onChange);
      this.form.addEventListener('submit', this.onSubmit);
      this.load();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    async load() {
      try {
        const inline = this.querySelector('[data-vehicle-data]');
        if (inline?.textContent.trim()) {
          this.data = JSON.parse(inline.textContent);
        } else if (this.dataset.source) {
          const response = await fetch(this.dataset.source, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Vehicle data could not be loaded');
          this.data = await response.json();
        }
        if (!Array.isArray(this.data)) this.data = [];
        this.data = this.data.map((item) => ({
          make: String(item.make || item.manufacturer || '').trim(),
          model: String(item.model || '').trim(),
          year: String(item.year || item.years || '').trim(),
          engine: String(item.engine || item.variant || '').trim(),
          result_url: String(item.result_url || item.url || '').trim()
        })).filter((item) => item.make || item.model || item.year || item.engine);

        this.populate(this.make, this.unique(this.data.map((item) => item.make)), this.make?.dataset.placeholder);
        this.populate(this.model, [], this.model?.dataset.placeholder);
        this.populate(this.year, [], this.year?.dataset.placeholder);
        this.populate(this.engine, [], this.engine?.dataset.placeholder);
        this.restore();
        this.toggleReady(true);
      } catch (error) {
        this.data = [];
        this.toggleReady(true);
        this.setStatus(this.dataset.loadError || error.message, true);
      }
    }

    unique(values) {
      return [...new Set(values.filter((value) => value !== undefined && value !== null && value !== ''))]
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    }

    populate(select, values, placeholder) {
      if (!select) return;
      const selected = select.value;
      select.replaceChildren(new Option(placeholder || 'Select', ''));
      values.forEach((value) => select.add(new Option(value, value)));
      if ([...select.options].some((option) => option.value === selected)) select.value = selected;
      select.disabled = values.length === 0;
    }

    matches(item, fields) {
      return fields.every(([key, value]) => !value || normalize(item[key]) === normalize(value));
    }

    onChange(event) {
      if (event.target === this.make) {
        const rows = this.make.value ? this.filteredRows(['make']) : [];
        this.populate(this.model, this.unique(rows.map((item) => item.model)), this.model?.dataset.placeholder);
        this.populate(this.year, [], this.year?.dataset.placeholder);
        this.populate(this.engine, [], this.engine?.dataset.placeholder);
      } else if (event.target === this.model) {
        const rows = this.model.value ? this.filteredRows(['make', 'model']) : [];
        this.populate(this.year, this.unique(rows.map((item) => item.year)), this.year?.dataset.placeholder);
        this.populate(this.engine, [], this.engine?.dataset.placeholder);
      } else if (event.target === this.year) {
        const rows = this.year.value ? this.filteredRows(['make', 'model', 'year']) : [];
        this.populate(this.engine, this.unique(rows.map((item) => item.engine)), this.engine?.dataset.placeholder);
      }
      if ([this.make, this.model, this.year, this.engine].includes(event.target) && this.natural) {
        this.natural.value = '';
      }
      this.setStatus('');
      this.dispatchEvent(new CustomEvent('vehicle:change', { bubbles: true, detail: this.selection() }));
    }

    filteredRows(keys) {
      const selection = this.selection();
      return this.data.filter((item) => this.matches(item, keys.map((key) => [key, selection[key]])));
    }

    selection() {
      return {
        make: this.make?.value || '',
        model: this.model?.value || '',
        year: this.year?.value || '',
        engine: this.engine?.value || ''
      };
    }

    onSubmit(event) {
      const naturalQuery = this.natural?.value.trim() || '';
      const selection = this.selection();

      if (naturalQuery) {
        const naturalMatch = this.findNaturalMatch(naturalQuery);
        if (naturalMatch) {
          event.preventDefault();
          this.save(naturalMatch);
          this.redirect(naturalMatch, naturalQuery);
        }
        // With no inline match, preserve the form's normal Shopify search.
        return;
      }

      const structuredFields = [this.make, this.model, this.year].filter(Boolean);
      if (structuredFields.length && structuredFields.some((field) => !field.value)) {
        event.preventDefault();
        this.setStatus(this.dataset.incompleteMessage || 'Select a manufacturer, model and year.', true);
        structuredFields.find((field) => !field.value)?.focus();
        return;
      }

      if (selection.make || selection.model || selection.year || selection.engine) {
        event.preventDefault();
        const exact = this.data.find((item) => this.matches(item, Object.entries(selection)));
        const chosen = exact || selection;
        this.save(chosen);
        this.redirect(chosen);
      }
    }

    findNaturalMatch(query) {
      const normalizedQuery = normalize(query);
      if (!normalizedQuery || !this.data.length) return null;
      const tokens = normalizedQuery.split(' ');
      const compactQuery = normalizedQuery.replace(/\s/g, '');
      const ranked = this.data.map((item) => {
        const fields = [item.year, item.make, item.model, item.engine].filter(Boolean);
        const haystack = normalize(fields.join(' '));
        const compact = haystack.replace(/\s/g, '');
        const allTokensMatch = tokens.every((token) => haystack.includes(token) || compact.includes(token));
        if (!allTokensMatch) return { item, score: -1 };
        const exactFields = fields.reduce((score, field) => score + (normalizedQuery.includes(normalize(field)) ? 2 : 0), 0);
        const score = exactFields + tokens.length + (compact.includes(compactQuery) ? 4 : 0);
        return { item, score };
      }).filter((entry) => entry.score >= 0).sort((a, b) => b.score - a.score);
      if (!ranked.length) return null;
      if (ranked.length === 1) return ranked[0].item;

      // Avoid choosing an arbitrary year/engine when a broad query matches
      // several records (for example, "BMW S1000RR").
      const queryContains = (value) => {
        const field = normalize(value);
        return field && (normalizedQuery.includes(field) || compactQuery.includes(field.replace(/\s/g, '')));
      };
      const best = ranked[0].item;
      return {
        make: queryContains(best.make) ? best.make : '',
        model: queryContains(best.model) ? best.model : '',
        year: queryContains(best.year) ? best.year : '',
        engine: queryContains(best.engine) ? best.engine : '',
        result_url: ''
      };
    }

    save(selection) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selection)); } catch (_) {}
      this.dispatchEvent(new CustomEvent('vehicle:selected', { bubbles: true, detail: selection }));
    }

    redirect(selection, fallbackQuery = '') {
      const collectionUrl = this.dataset.collectionUrl || '';
      if (collectionUrl) {
        const target = new URL(collectionUrl, location.origin);
        Object.entries(FILTERS).forEach(([key, parameter]) => {
          if (selection[key]) target.searchParams.set(parameter, selection[key]);
        });
        location.assign(target.toString());
        return;
      }

      if (selection.result_url) {
        location.assign(new URL(selection.result_url, location.origin).toString());
        return;
      }

      const target = new URL(this.form.action || window.theme?.routes?.search || '/search', location.origin);
      const query = fallbackQuery || [selection.year, selection.make, selection.model, selection.engine].filter(Boolean).join(' ');
      target.searchParams.set('q', query);
      target.searchParams.set('type', 'product');
      target.searchParams.set('options[prefix]', 'last');
      location.assign(target.toString());
    }

    restore() {
      let saved;
      try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (_) {}
      if (!saved) return;
      if (this.make) {
        this.make.value = saved.make || '';
        this.make.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.model) {
        this.model.value = saved.model || '';
        this.model.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.year) {
        this.year.value = saved.year || '';
        this.year.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.engine) this.engine.value = saved.engine || saved.variant || '';
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.toggleAttribute('data-error', error && Boolean(message));
      this.status.setAttribute('role', error && message ? 'alert' : 'status');
      this.status.setAttribute('aria-live', error && message ? 'assertive' : 'polite');
    }

    toggleReady(ready) {
      this.toggleAttribute('data-ready', ready);
      // Natural search remains usable even if fitment JSON fails to load.
      this.querySelectorAll('[data-vehicle-structured] button[type="submit"]').forEach((button) => {
        button.disabled = !ready;
      });
    }
  }

  if (!customElements.get('vehicle-finder')) customElements.define('vehicle-finder', VehicleFinder);

  document.addEventListener('click', (event) => {
    const reset = event.target.closest('[data-vehicle-reset]');
    if (!reset) return;
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    const finder = reset.closest('vehicle-finder');
    finder?.form?.reset();
    finder?.make?.dispatchEvent(new Event('change', { bubbles: true }));
    finder?.setStatus('');
    document.dispatchEvent(new CustomEvent('vehicle:cleared'));
  });
})();
